import type React from 'react';
import { useHref, useNavigate } from 'react-router-dom';
import { RouterProvider } from 'react-aria-components';

/**
 * React Aria's router seam, wired to react-router (BOS-130).
 *
 * Every HeroUI component built on React Aria that takes an `href` — the
 * `/accounting` tab strips are the first — renders a real `<a>`, which is what
 * gives it middle-click, Ctrl/Cmd+click and the browser's own "open link in new
 * tab" context menu for free. **Without this provider that anchor is inert:**
 * React Aria intercepts the plain click and has nowhere to send it, so
 * left-clicking a tab does nothing at all.
 *
 * With it:
 *
 * - a **plain** click goes to react-router's `navigate` — SPA, no reload;
 * - a **modified** click (middle, Ctrl/Cmd, Shift) is left to the browser,
 *   which is what makes "open in a new tab" work without the app
 *   reimplementing it;
 * - keyboard activation navigates the same way a plain click does.
 *
 * `useHref` is passed so a path resolves through react-router's basename rather
 * than being trusted raw.
 *
 * It must be rendered **inside** the react-router tree, because both hooks it
 * depends on are react-router's. `App` mounts it at the root; the tab-shell
 * tests mount it in their own harness for the same reason a browser gets it —
 * a shell rendered without it has dead tabs, and a test that did not include it
 * would be asserting against a page no user ever sees.
 */
export const AriaRouter: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const navigate = useNavigate();

    return (
        <RouterProvider navigate={navigate} useHref={useHref}>
            {children}
        </RouterProvider>
    );
};
